import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';

// SmartComContacts - detail page for the SmartCom tile. v0.0.1: the merged CRM
// view (native SmartCom contacts + SmartShop contacts via the smartcom->smartshop
// grant) plus create/edit of native contacts. Against the live contract proven
// on GIDO_IE_0007, 2026-08-22.
//
// -- Tenancy (D-084) -------------------------------------------------------
// Both endpoints take gido_id as a VERIFIED PARAMETER and prove the caller's seat in that org
// with resolve_gidu. This page therefore SENDS gido_id (from the signed-in gido prop). It is not
// a trust hole: naming an org you hold no seat in is refused 403. A login may hold seats in
// several orgs (D-082), so the org cannot be derived from the uid - a seat holder has no
// gido_registry record at all. jurisdiction is still server-derived and never sent.
//
// -- Write auth (save-contact) --------------------------------------------
// Authorization: Bearer <firebase_id_token> ONLY. Server checks: valid gido_id
// on record + twofa_enabled + grants.smartcom.access in {read|write} (write
// required to save). Deny reasons: unauthenticated (401); no_identity /
// incomplete_identity (401); not_entitled (403).
//
// -- The two-grade access model, surfaced in the UI (D-049) ----------------
// Each contact carries `editable`. Native (domain: smartcom) rows are editable
// by the owner; SmartShop-sourced rows are read-only here (SmartCom does not
// cross-write another solution's store - D-006; master-data propagation D-053
// is reserved, not built). The UI honours `editable` - it never offers an edit
// control on a row the backend would refuse.

const LIST_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartcom-list-contacts';
const SAVE_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartcom-save-contact';
const DETAIL_URL = 'https://europe-west1-dbos-core-prod.cloudfunctions.net/smartcom-contact-detail';

// The grant-eligible + relationship statuses (dbos-consumer status enum). Marketing-stage
// (lead/prospect) and ended (former) are shown but flagged non-eligible server-side.
const STATUSES = [
  'lead', 'prospect', 'customer', 'partner', 'supplier', 'authority', 'certificator', 'former',
];

function emptyForm() {
  return { name: '', email: '', phone: '', vat: '', status: '', consent_marketing: false };
}

function contactToForm(c) {
  return {
    name: c.name || '',
    email: c.email || '',
    phone: c.phone || '',
    vat: c.vat || '',
    status: c.status || '',
    consent_marketing: !!(c.consent && c.consent.marketing && c.consent.marketing.granted),
  };
}

function DenyMessage({ reason }) {
  const map = {
    unauthenticated: 'Your session expired. Please sign in again.',
    no_identity: 'No account record found for this login.',
    incomplete_identity: 'Your account record is incomplete. Contact support.',
    not_entitled: 'This account is not licensed for SmartCom. Contact hello@dbos.world for a test voucher.',
  };
  return <div className="sf-panel-error">{map[reason] || `Denied: ${reason}`}</div>;
}

function SourceBadge({ sources }) {
  const domains = (sources || []).map((s) => s.domain);
  return (
    <span className="scom-sources">
      {domains.map((d) => (
        <span key={d} className={`scom-source scom-source-${d}`}>{d}</span>
      ))}
    </span>
  );
}

// -- Create / edit form (native contacts only) ------------------------------
function ContactForm({ gido, initial, onSaved, onCancel }) {
  const [form, setForm]         = useState(initial || emptyForm());
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [denyReason, setDeny]   = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setSaving(true); setError(null); setDeny(null);

    // at least one identifier - resolve_or_mint needs vat/email/name (mirror of the backend rule)
    if (!form.name.trim() && !form.email.trim() && !form.vat.trim()) {
      setError('Give at least a name, an email, or a VAT number.');
      setSaving(false);
      return;
    }

    const body = {
      gido_id: gido?.gido_id,          // D-084: named by the caller, seat-proven server-side
      name: form.name.trim() || undefined,
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      vat: form.vat.trim() || undefined,
      status: form.status || undefined,
      consent_marketing: form.consent_marketing,
    };
    // territory is never sent - the server derives jurisdiction from the registry
    // D-084: gido_id is sent and seat-proven server-side; territory is never sent

    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(SAVE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        onSaved(data.gid);
      } else if (res.status === 401 || res.status === 403) {
        setDeny(data?.reason || data?.error || 'denied');
      } else if (data?.reason === 'identity_conflict') {
        setError('This contact matches more than one existing record. Resolve the duplicate before saving.');
      } else {
        setError(data?.error || data?.reason || 'Could not save contact.');
      }
    } catch (e) {
      setError('Network error. Try again.');
    }
    setSaving(false);
  }

  return (
    <div className="sf-panel">
      <div className="sf-panel-header">
        <h3>{initial ? 'Edit contact' : 'New contact'}</h3>
        <div>
          <button className="sf-btn sf-btn-small" onClick={onCancel} disabled={saving}>Cancel</button>{' '}
          <button className="sf-btn" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>

      {denyReason && <DenyMessage reason={denyReason} />}
      {error && <div className="sf-panel-error">{error}</div>}

      <div className="ss-form-grid">
        <label>Name<input type="text" value={form.name} onChange={(e) => set('name', e.target.value)} /></label>
        <label>Email<input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} /></label>
        <label>Phone<input type="text" value={form.phone} onChange={(e) => set('phone', e.target.value)} /></label>
        <label>VAT (business)<input type="text" value={form.vat} onChange={(e) => set('vat', e.target.value)} /></label>
        <label>Status
          <select value={form.status} onChange={(e) => set('status', e.target.value)}>
            <option value="">(none)</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="ss-checkbox">
          <input type="checkbox" checked={form.consent_marketing}
                 onChange={(e) => set('consent_marketing', e.target.checked)} /> Marketing consent
        </label>
      </div>
    </div>
  );
}

// -- Contact detail: the contact plus every card its owning solution generates (D-087) -------
function money(cents, currency) {
  if (cents === null || cents === undefined) return '-';
  const cur = (currency || 'eur').toUpperCase();
  const sym = cur === 'EUR' ? '\u20ac' : cur === 'GBP' ? '\u00a3' : cur === 'USD' ? '$' : '';
  return `${sym}${(cents / 100).toFixed(2)}`;
}

function when(iso) {
  if (!iso) return '-';
  const d = new Date(iso);
  return isNaN(d) ? '-' : d.toLocaleDateString();
}

function ContactDetail({ gido, contact, onBack, onEdit }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch(DETAIL_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ gido_id: gido?.gido_id, gid: contact.gid }),
        });
        const body = await res.json().catch(() => ({}));
        if (!live) return;
        if (res.ok && body.ok) setData(body);
        else setError(body?.error || body?.reason || 'Could not load this contact.');
      } catch (e) {
        if (live) setError('Network error. Try again.');
      }
      if (live) setLoading(false);
    })();
    return () => { live = false; };
  }, [gido, contact]);

  const shop = data?.cards?.smartshop;
  const sum  = shop?.summary || {};

  return (
    <div className="detail-page">
      <div className="detail-header">
        <button className="sf-btn sf-btn-small" onClick={onBack}>&larr; Contacts</button>
        <span className="header-title">{contact.name || contact.email || 'Contact'}</span>
        {contact.editable && (
          <button className="sf-btn" onClick={() => onEdit(contact)}>Edit</button>
        )}
      </div>

      {error && <div className="sf-panel-error">{error}</div>}

      <div className="sf-panel">
        <div className="sf-panel-header"><h3>Contact</h3></div>
        <div className="scom-detail-grid">
          <div><label>Name</label><span>{contact.name || '-'}</span></div>
          <div><label>Email</label><span>{contact.email || '-'}</span></div>
          <div><label>Phone</label><span>{contact.phone || '-'}</span></div>
          <div><label>Status</label><span>{contact.status || '-'}</span></div>
          <div><label>Source</label><span><SourceBadge sources={contact.sources} /></span></div>
        </div>
      </div>

      {loading ? (
        <div className="sf-panel">Loading history...</div>
      ) : shop?.available === false ? (
        <div className="sf-panel scom-ghost-note">
          Shop history unavailable ({shop.reason}).
        </div>
      ) : shop ? (
        <>
          <div className="sf-panel">
            <div className="sf-panel-header"><h3>Purchase history</h3></div>
            <div className="scom-detail-grid">
              <div><label>Orders</label><span>{sum.order_count ?? 0}</span></div>
              <div><label>Lifetime total</label><span>{money(sum.lifetime_total, sum.currency)}</span></div>
              <div><label>First order</label><span>{when(sum.first_order_at)}</span></div>
              <div><label>Last order</label><span>{when(sum.last_order_at)}</span></div>
            </div>
            {(shop.orders || []).length === 0 ? (
              <div className="scom-ghost-note">No orders yet.</div>
            ) : (
              <table className="scom-table">
                <thead><tr><th>Date</th><th>Order</th><th>Items</th><th>Total</th><th>Status</th></tr></thead>
                <tbody>
                  {shop.orders.map((o) => (
                    <tr key={o.order_id}>
                      <td>{when(o.created_at)}</td>
                      <td>{o.order_id}</td>
                      <td>{(o.items || []).map((i) => `${i.qty}x ${i.name}`).join(', ') || '-'}</td>
                      <td>{money(o.total, o.currency)}</td>
                      <td>{o.status || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="sf-panel">
            <div className="sf-panel-header"><h3>Saved carts</h3></div>
            {(shop.carts || []).length === 0 ? (
              <div className="scom-ghost-note">No saved carts.</div>
            ) : (
              <table className="scom-table">
                <thead><tr><th>Saved</th><th>Items</th><th>Value</th></tr></thead>
                <tbody>
                  {shop.carts.map((c) => (
                    <tr key={c.cart_id}>
                      <td>{when(c.updated_at)}</td>
                      <td>{(c.items || []).map((i) => `${i.qty}x ${i.name}`).join(', ') || `${c.item_count} items`}</td>
                      <td>{money(c.total, c.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

// -- The page ---------------------------------------------------------------
function SmartComContacts({ gido, onBack }) {
  const [contacts, setContacts]   = useState([]);
  const [ghosts, setGhosts]       = useState({});
  const [shopAccess, setShop]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [denyReason, setDeny]     = useState(null);
  const [editing, setEditing]     = useState(null);   // null | 'new' | contact object
  const [viewing, setViewing]     = useState(null);   // null | contact object (detail pane)

  async function load() {
    setLoading(true); setError(null); setDeny(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const res = await fetch(LIST_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ gido_id: gido?.gido_id }),   // D-084: named by the caller, seat-proven server-side
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.contacts)) {
        setContacts(data.contacts);
        setGhosts(data.ghost_counts || {});
        setShop(data.shop_access || null);
      } else if (res.status === 401 || res.status === 403) {
        setDeny(data?.reason || data?.error || 'denied');
      } else {
        setError(data?.error || 'Could not load contacts.');
      }
    } catch (e) {
      setError('Network error. Try again.');
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const ghostTotal = Object.values(ghosts).reduce((a, b) => a + b, 0);

  if (viewing && !editing) {
    return (
      <ContactDetail
        gido={gido}
        contact={viewing}
        onBack={() => setViewing(null)}
        onEdit={(c) => setEditing(c)}
      />
    );
  }

  if (editing) {
    return (
      <div className="detail-page">
        <div className="detail-header">
          <button className="sf-btn sf-btn-small" onClick={() => setEditing(null)}>&larr; Contacts</button>
          <span className="header-title">SmartCom</span>
        </div>
        <ContactForm
          gido={gido}
          initial={editing === 'new' ? null : contactToForm(editing)}
          onSaved={() => { setEditing(null); setViewing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      </div>
    );
  }

  return (
    <div className="detail-page">
      <div className="detail-header">
        <button className="sf-btn sf-btn-small" onClick={onBack}>&larr; Back</button>
        <span className="header-title">SmartCom - Contacts</span>
        <button className="sf-btn" onClick={() => setEditing('new')}>+ New contact</button>
      </div>

      {denyReason && <DenyMessage reason={denyReason} />}
      {error && <div className="sf-panel-error">{error}</div>}

      {loading ? (
        <div className="sf-panel">Loading contacts...</div>
      ) : (
        <>
          {contacts.length === 0 && !denyReason && (
            <div className="sf-panel">No contacts yet. Add your first with "+ New contact".</div>
          )}

          {contacts.length > 0 && (
            <table className="scom-table">
              <thead>
                <tr>
                  <th>Name</th><th>Email</th><th>Phone</th><th>Status</th><th>Source</th><th></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c) => (
                  <tr key={c.gid}>
                    <td>
                      <button className="scom-link" onClick={() => setViewing(c)}>
                        {c.name || c.email || c.gid}
                      </button>
                    </td>
                    <td>{c.email || '-'}</td>
                    <td>{c.phone || '-'}</td>
                    <td>{c.status || '-'}</td>
                    <td><SourceBadge sources={c.sources} /></td>
                    <td>
                      {c.editable
                        ? <button className="sf-btn sf-btn-small" onClick={() => setEditing(c)}>Edit</button>
                        : <span className="scom-readonly" title="Read-only - owned by another solution">read-only</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Ghost Count (D-019): contacts outside the grant scope - count only, never content */}
          {ghostTotal > 0 && (
            <div className="scom-ghost-note">
              {ghostTotal} further contact{ghostTotal === 1 ? '' : 's'} exist outside your current
              access scope and are not shown.
            </div>
          )}

          {shopAccess && shopAccess !== 'ok' && (
            <div className="scom-ghost-note">
              SmartShop contacts are not included ({shopAccess}).
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default SmartComContacts;